<script setup lang="ts">
/**
 * 带标签/提示/错误的文本输入。
 *
 * 三个细节都是有理由的，别改：
 * 1. `inheritAttrs: false` + `v-bind="$attrs"` 打到 `<input>` 上，而不是落在包裹的
 *    `<div>` 上——否则 `placeholder`、`maxlength` 这些原生属性会粘在容器上，
 *    表现得像"没生效"。
 * 2. **错误优先于提示**：两者同时存在时只显示错误。同时显示两行文案会把
 *    "输入框为什么变红"和"格式要求"混在一起。
 * 3. `aria-describedby` 指向真正被渲染出来的那一段，而不是固定指向 hint。
 */

import { computed, ref, useId } from 'vue';

import { cn } from '../../lib/cn';

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  label: string;
  hint?: string;
  error?: string | null;
  id?: string;
  type?: string;
  /** 受控值（`v-model`）。不传时相当于一个普通原生输入。 */
  modelValue?: string;
}>();

/**
 * 必须显式声明并转发：`v-model` 在组件上编译成 `modelValue` 属性 +
 * `update:modelValue` 事件，而**原生 `<input>` 不会自己发这个事件**。
 * 少了这一段，`v-model="phone"` 会静默失效（值永远不变），
 * `@update:model-value` 也永远不触发——输入框看起来能打字，状态却是空的。
 */
const emit = defineEmits<{ 'update:modelValue': [value: string] }>();

function handleInput(event: Event): void {
  const target = event.target;
  emit('update:modelValue', target instanceof HTMLInputElement ? target.value : '');
}

const generated = useId();
const inputId = computed(() => props.id ?? generated);
const invalid = computed(() => props.error !== null && props.error !== undefined && props.error.length > 0);

const inputRef = ref<HTMLInputElement | null>(null);
defineExpose({
  focus: () => inputRef.value?.focus(),
});

const describedBy = computed(() => {
  if (invalid.value) return `${inputId.value}-error`;
  if (props.hint !== undefined) return `${inputId.value}-hint`;
  return undefined;
});
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <label :for="inputId" class="text-sm font-medium text-slate-700">{{ props.label }}</label>
    <input
      ref="inputRef"
      :id="inputId"
      v-bind="$attrs"
      :type="props.type ?? 'text'"
      :value="props.modelValue ?? ''"
      @input="handleInput"
      :aria-invalid="invalid ? 'true' : undefined"
      :aria-describedby="describedBy"
      :class="
        cn(
          'h-10 w-full rounded-xl border bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400',
          'focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400',
          invalid ? 'border-safety-400' : 'border-slate-200',
          $attrs.class as string,
        )
      "
    />
    <p v-if="invalid" :id="`${inputId}-error`" role="alert" class="text-xs text-safety-600">
      {{ props.error }}
    </p>
    <p v-else-if="props.hint !== undefined" :id="`${inputId}-hint`" class="text-xs text-slate-500">
      {{ props.hint }}
    </p>
  </div>
</template>
